import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Server, Globe, ChevronRight, RefreshCw, AlertTriangle, PlusCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { DomainSelector } from './DomainSelector';
import { ServiceManagement } from './ServiceManagement';
import { Network, Domain } from '../../types';
import axiosInstance from '../../lib/api/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { QuickAddServiceModal } from './QuickAddServiceModal';
import { ServiceType, SERVICE_TO_SOLUTIONS, ServiceSolution } from '../../types/service';

interface AuditMissingService {
  domainId: number;
  domain: string;
  network: string;
  missingService: string; // ServiceType string
  identifierUsed: string;
}

interface AuditResponse {
  meta: {
    startDate: string;
    endDate: string;
    totals: Record<string, number>;
  };
  missingServices: AuditMissingService[];
}

export const DomainServices: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [networks, setNetworks] = useState<Network[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditData, setAuditData] = useState<AuditResponse | null>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [selectedMissing, setSelectedMissing] = useState<AuditMissingService | null>(null);
  const [quickAddDomain, setQuickAddDomain] = useState<Domain | null>(null);
  const [quickAddDefaults, setQuickAddDefaults] = useState<any | null>(null);
  const [reloadSignal, setReloadSignal] = useState(0);

  const loadAudit = async () => {
    try {
      setAuditLoading(true);
      setAuditError(null);
      const { data } = await axiosInstance.get<AuditResponse>('/services/audit');
      setAuditData(data);
    } catch (e: any) {
      console.error('Audit load error', e);
      setAuditError(e?.response?.data?.error || 'Audit error');
    } finally {
      setAuditLoading(false);
    }
  };

  // Ouverture création rapide service manquant
  const openQuickAdd = (missing: AuditMissingService) => {
    console.log('🚀 openQuickAdd called with:', missing);
    setSelectedMissing(missing);
    let domainObj: Domain | null = null;
    
    // Pour les services avec domainId = 0 (sections Outbrain), pas de domaine pré-sélectionné
    if (missing.domainId !== 0) {
      console.log('🔍 Searching for domain with ID:', missing.domainId);
      console.log('📊 Available networks:', networks.length);
      
      for (const n of networks) {
        const domains = (n as any).domains || [];
        console.log(`🌐 Network ${n.name} has ${domains.length} domains`);
        const d = domains.find((dom: Domain) => dom.id === missing.domainId);
        if (d) { 
          domainObj = d; 
          console.log('✅ Found domain:', d.name);
          break; 
        }
      }
      
      if (!domainObj) {
        console.error('❌ Domain not found for ID:', missing.domainId);
        console.log('Available domain IDs:', networks.flatMap(n => ((n as any).domains || []).map((d: Domain) => d.id)));
        return; // Domaine requis mais non trouvé
      }
    } else {
      console.log('🎯 Section Outbrain, no domain required');
    }
    
    console.log('✅ Opening QuickAdd modal');
    setQuickAddDomain(domainObj);
    const serviceType = missing.missingService as ServiceType;
    const allowed = SERVICE_TO_SOLUTIONS[serviceType] || [];
    setQuickAddDefaults({
      service: serviceType,
      solution: allowed[0] as ServiceSolution | undefined,
      websiteNameMapping: missing.identifierUsed
    });
    setQuickAddOpen(true);
  };

  const handleQuickAddSaved = async () => {
    await loadAudit();
    if (selectedDomain && selectedMissing && selectedDomain.id === selectedMissing.domainId) {
      setReloadSignal(prev => prev + 1);
    }
  };

  // Récupérer réseaux + domaines
  const fetchNetworksWithDomains = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Récupérer les réseaux
      const networksResponse = await axiosInstance.get<Network[]>('/networks');
      const networksData = networksResponse.data || [];
      
      // Pour chaque réseau, récupérer ses domaines
      const networksWithDomains = await Promise.all(
        networksData.map(async (network) => {
          try {
            const domainsResponse = await axiosInstance.get<Domain[]>(`/domains/network/${network.id}`);
            const domains = domainsResponse.data || [];
            return {
              ...network,
              domains: domains.map(domain => ({
                id: domain.id,
                name: domain.name,
                url: domain.url,
                networkId: domain.networkId,
                createdAt: (domain as any).createdAt || new Date().toISOString(),
                updatedAt: (domain as any).updatedAt || new Date().toISOString()
              }))
            };
          } catch (error) {
            console.error(`Error fetching domains for network ${network.name}:`, error);
            return { ...network, domains: [] };
          }
        })
      );
      setNetworks(networksWithDomains);
    } catch (error) {
      console.error('Error fetching networks:', error);
      setError(t('domainServices.errorFetchingData'));
    } finally {
      setIsLoading(false);
    }
  };

  // Chargements initiaux
  useEffect(() => {
    fetchNetworksWithDomains();
    loadAudit();
  }, []);

  // Permissions
  if (!currentUser || currentUser.role !== 'ADMIN') {
    return (
      <div className="p-6">
        <Card><CardContent className="py-6"><div className="text-destructive text-xl font-semibold">{t('common.accessDenied')}</div></CardContent></Card>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /><CardTitle className="text-2xl">{t('domainServices.title')}</CardTitle></div>
          </CardHeader>
          <CardContent><div className="text-muted-foreground">{t('common.loading')}</div></CardContent>
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /><CardTitle className="text-2xl">{t('domainServices.title')}</CardTitle></div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive"><AlertTitle>{t('common.error')}</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Regroupement par type de service manquant
  const grouped: Record<string, AuditMissingService[]> = { ADBLOCK: [], NO_CONSENT: [], SSP: [] };
  auditData?.missingServices?.forEach(ms => { if (!grouped[ms.missingService]) grouped[ms.missingService] = []; grouped[ms.missingService].push(ms); });

  const renderMissingTable = (serviceKey: ServiceType, title: string) => {
    const rows = grouped[serviceKey] || [];
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" /> {title}
            {auditLoading && <span className="text-xs text-muted-foreground">{t('common.loading')}</span>}
          </h4>
          {rows.length > 0 && <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{rows.length}</span>}
        </div>
        <div className="border rounded-md overflow-hidden flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-background border-b sticky top-0 z-10 shadow-sm">
                <tr><th className="text-left px-2 py-1 w-1/2 font-medium">{t('common.domain','Domaine')}</th><th className="text-left px-2 py-1 font-medium">{t('common.actions','Actions')}</th></tr>
              </thead>
              <tbody className="bg-background">
                {rows.length ? rows.map(r => (
                  <tr key={`${r.domainId}-${r.missingService}-${r.identifierUsed}`} className="border-t hover:bg-muted/30">
                    <td className="px-2 py-1 truncate" title={r.domain}>{r.domain}</td>
                    <td className="px-2 py-1">
                      <button onClick={() => openQuickAdd(r)} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border rounded hover:bg-accent transition-colors">
                        <PlusCircle className="h-3 w-3" /> {t('common.add','Ajouter')}
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={2} className="px-2 py-2 text-muted-foreground text-center text-[11px]">{auditLoading ? t('common.loading') : t('domainServices.audit.none','Aucun')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-7rem)] overflow-hidden p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
        {/* Colonne gauche : audit groupé */}
        <div className="lg:col-span-1 h-full flex flex-col overflow-hidden">
          <Card className="border-none shadow-sm dark:shadow-lg h-full flex flex-col">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <CardTitle className="text-lg">{t('domainServices.audit.title','Audit (30j)')}</CardTitle>
              </div>
              <button onClick={loadAudit} disabled={auditLoading} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border hover:bg-accent disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${auditLoading ? 'animate-spin':''}`} />{t('common.refresh','Rafraîchir')}
              </button>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden space-y-3 pt-2">
              {auditError && <Alert variant="destructive"><AlertTitle>{t('common.error')}</AlertTitle><AlertDescription>{auditError}</AlertDescription></Alert>}
              {!auditError && (
                <div className="h-full flex flex-col gap-3">
                  <div className="flex-1 min-h-0">
                    {renderMissingTable(ServiceType.ADBLOCK, t('domainServices.audit.adblock','Adblock manquants'))}
                  </div>
                  <div className="flex-1 min-h-0">
                    {renderMissingTable(ServiceType.NO_CONSENT, t('domainServices.audit.noConsent','No Consent manquants'))}
                  </div>
                  <div className="flex-1 min-h-0">
                    {renderMissingTable(ServiceType.SSP, t('domainServices.audit.ssp','SSP manquants'))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {/* Colonne droite : panel adaptatif domaines / services */}
        <div className="lg:col-span-2 h-full flex flex-col overflow-hidden">
          <Card className="border-none shadow-sm dark:shadow-lg h-full flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3 flex-wrap">
                {selectedDomain ? (
                  <>
                    <button onClick={() => setSelectedDomain(null)} className="inline-flex items-center gap-1 text-sm px-2 py-1 border rounded hover:bg-accent">
                      <ArrowLeft className="h-4 w-4" /> {t('common.back','Retour')}
                    </button>
                    <Server className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{t('domainServices.servicesFor',{ domain: selectedDomain.name })}</CardTitle>
                    <div className="flex items-center text-sm text-muted-foreground ml-auto">
                      <span>{selectedDomain.url}</span>
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </>
                ) : (
                  <>
                    <Globe className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{t('domainServices.selectDomain')}</CardTitle>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden pt-4">
              {selectedDomain ? (
                <ServiceManagement domain={selectedDomain} reloadSignal={reloadSignal} />
              ) : (
                <div className="h-full flex flex-col lg:flex-row gap-6">
                  <div className="lg:w-72 w-full border rounded-md p-3 flex flex-col overflow-hidden">
                    <div className="text-sm font-medium mb-2">{t('domainServices.selectDomainFirst')}</div>
                    <div className="flex-1 overflow-auto pr-1">
                      <DomainSelector networks={networks} selectedDomain={selectedDomain} onDomainSelect={setSelectedDomain} />
                    </div>
                  </div>
                  <div className="flex-1 hidden lg:flex items-center justify-center text-center px-6">
                    <div>
                      <Globe className="h-16 w-16 text-muted-foreground/30 mb-4 mx-auto" />
                      <h3 className="text-lg font-semibold text-muted-foreground mb-2">{t('domainServices.noDomainSelected')}</h3>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">{t('domainServices.selectDomainToManage')}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {quickAddOpen && quickAddDefaults && selectedMissing && (
        <QuickAddServiceModal
          open={quickAddOpen}
          onOpenChange={setQuickAddOpen}
          domain={quickAddDomain}
          missingService={selectedMissing}
          onCreated={handleQuickAddSaved}
        />
      )}
    </div>
  );
};